import { cancelForAnyReason, flexibleTicket } from '@eti/products';
import { constants } from '@eti/utils';
import { useProperty, useTranslation } from '@eti/providers';
import flatMapDeep from 'lodash/flatMapDeep';
import sumBy from 'lodash/sumBy';
import { AD_FORM_POSITION_IN_RESULT_LIST } from '../constants/resultConstants';
import { quickSortType } from '../../../constants/quickSortConstants';
import { tripTagTitles } from './texts';

const { ageTypes, priceDisplayTypes } = constants;
const { PER_ADULT, PER_TRAVELER, TOTAL } = priceDisplayTypes;

export const getMinNumberOfSeats = (bounds) => {
  const minNumberOfSeats = bounds?.map((bound) =>
    bound.segments
      .filter((segment) => segment.__typename === 'TripSegment')
      .map((segment) =>
        segment.segmentDetails?.map((segmentDetail) => segmentDetail.numberOfSeatsLeft),
      ),
  );

  return Math.min(...flatMapDeep(minNumberOfSeats));
};

export const getDiscountedPaymentMethod = (paymentMethodPrices = []) => {
  if (!paymentMethodPrices.length) {
    return { hasDiscountedPrice: false };
  }
  const paymentMethod = paymentMethodPrices.reduce((prev, current) =>
    prev.price.value < current.price.value ? prev : current,
  );
  const hasDiscountedPrice = paymentMethod?.price?.value < 0;
  return { ...paymentMethod, hasDiscountedPrice };
};

export const shouldShowSeatsLeftNotification = (
  numberOfSeatsLeft,
  numberOfTravelers,
  seatsThreshold,
) =>
  Boolean(
    numberOfSeatsLeft > 0 &&
      numberOfSeatsLeft < seatsThreshold &&
      numberOfSeatsLeft >= numberOfTravelers,
  );

export const isTicketIncluded = (products = [], ticketID) =>
  products.some((product) => Number(product.productId) === ticketID && product.selectedWithTrip);

export const getFlexibleTicketConfig = (products = []) =>
  products.find((product) => Number(product.productId) === flexibleTicket.id);

export const getCFARTicketConfig = (products = []) =>
  products.find((product) => Number(product.productId) === cancelForAnyReason.id);

export const getAirlines = (bound) => {
  const airlines = bound.segments
    .filter((segment) => segment.__typename === 'TripSegment')
    .map((segment) => segment.marketingCarrier.code);
  return Array.from(new Set(airlines));
};

export const isFlexibleTicketAllowedForCarriers = (bounds, carriersToHideFlexTicket) => {
  if (!carriersToHideFlexTicket) {
    return true;
  }
  if (carriersToHideFlexTicket.toUpperCase() === 'ALL') {
    return false;
  }
  const tripCarriers = Array.from(new Set(bounds.flatMap((bound) => getAirlines(bound))));
  const hiddenCarriers = carriersToHideFlexTicket.split(',');
  return !tripCarriers.some((tripCarrier) => hiddenCarriers.includes(tripCarrier));
};

export const isCFARTicketAllowedForCarriers = (bounds, carriersToHideCFARTicket) => {
  if (!carriersToHideCFARTicket) {
    return true;
  }
  if (carriersToHideCFARTicket.toUpperCase() === 'ALL') {
    return false;
  }
  const tripCarriers = Array.from(new Set(bounds.flatMap((bound) => getAirlines(bound))));
  const hiddenCarriers = carriersToHideCFARTicket.split(',');
  return !tripCarriers.some((tripCarrier) => hiddenCarriers.includes(tripCarrier));
};

const getAdultPrice = (travelerPrices) => {
  const adultTraveler =
    Array.isArray(travelerPrices) &&
    travelerPrices.find((travelerPrice) => travelerPrice.ageType === ageTypes.ADULT);

  return adultTraveler ? adultTraveler.totalPrice : travelerPrices;
};

const getFlexibleTicketAdultPrice = (flexibleTicketPrices = []) =>
  flexibleTicketPrices.reduce(
    (price, currentPrice) =>
      currentPrice.price.price.value > price ? currentPrice.price.price.value : price,
    0,
  );

export const getTripPricePerAdult = (
  travelerPrices = [],
  flexibleTicketPrices = [],
  cfarTicketPrices = 0,
) => {
  const adultPrice = getAdultPrice(travelerPrices);
  const flexibleTicketPrice = getFlexibleTicketAdultPrice(flexibleTicketPrices);
  return adultPrice + flexibleTicketPrice + cfarTicketPrices;
};

export const getTripTotalPrice = (
  travelerPrices = [],
  flexibleTicketPrices = [],
  cfarTicketPrices = 0,
) =>
  sumBy(travelerPrices, 'totalPriceSum') +
  sumBy(flexibleTicketPrices, 'price.price.value') +
  cfarTicketPrices;

export const getTripTotalRebookingPrice = (travelerPrices = []) => {
  return travelerPrices.reduce(
    (totalPrice, travelerPrice) =>
      totalPrice + travelerPrice.totalDifference * travelerPrice.quantity,
    0,
  );
};

export const getTripTotalPriceWithDiscount = (
  travelerPrices = [],
  flexibleTicketPrices = [],
  cfarTicketPrices = 0,
  discountAmount = 0,
) =>
  Math.max(
    0,
    getTripTotalPrice(travelerPrices, flexibleTicketPrices, cfarTicketPrices) - discountAmount,
  );

export const getTripPricePerTraveler = (
  travelerPrices = [],
  flexibleTicketPrices = [],
  cfarTicketPrices = 0,
) =>
  getTripTotalPrice(travelerPrices, flexibleTicketPrices, cfarTicketPrices) /
  sumBy(travelerPrices, 'quantity');

export const getTripDisplayPrice = (tripPriceDisplayType) => {
  switch (tripPriceDisplayType) {
    case priceDisplayTypes.TOTAL:
      return getTripTotalPrice;
    case priceDisplayTypes.PER_TRAVELER:
      return getTripPricePerTraveler;
    case priceDisplayTypes.PER_ADULT:
    default:
      return getTripPricePerAdult;
  }
};

export const usePriceDisplayType = (discountAmount = 0) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const priceDisplayType = discountAmount ? TOTAL : p('IbeClient.Result.ShowPrice.Type');
  const priceDisplayTexts = {
    [PER_ADULT]: t('Result.PricePerAdult.Text'),
    [PER_TRAVELER]: t('Result.PricePerPerson.Text'),
    [TOTAL]: t('Result.TotalPrice.Text'),
  };
  const priceDisplayText = priceDisplayTexts[priceDisplayType];
  return { priceDisplayType, priceDisplayText };
};

export const getTotalPriceForFlexibleTicket = (flexibleTicketPrices = []) =>
  flexibleTicketPrices.reduce((acc, curr) => acc + curr?.price?.price?.value ?? 0, 0);

export const getTripTagsTitle = (tripTags, translate) =>
  Array.isArray(tripTags)
    ? tripTags
        .reduce(
          (acc, tripTag) =>
            tripTagTitles[tripTag] ? [...acc, translate(tripTagTitles[tripTag])] : acc,
          [],
        )
        .join(' | ')
    : '';

export const getFlightResultToInsertAdFormAfter = (
  flightResultsWrapper,
  position = AD_FORM_POSITION_IN_RESULT_LIST,
) =>
  flightResultsWrapper &&
  flightResultsWrapper.children &&
  flightResultsWrapper.children[position - 1]
    ? flightResultsWrapper.children[position - 1]
    : null;

export const removeUndesiredTripsAndTags = (sponsoredTripsData = []) => {
  const desiredValues = ['WEIGHTED', 'PRICE', 'SPEED', 'STAY'];

  return sponsoredTripsData
    .filter((trip) => desiredValues.some((desiredValue) => trip.tripTags.includes(desiredValue)))
    .map((trip) => ({
      ...trip,
      tripTags: trip.tripTags.filter((tag) => desiredValues.includes(tag)),
    }));
};

export const shouldShowPartnerLogo = (partner, partnersToShowLogoFor) => {
  if (!partner || !partnersToShowLogoFor) {
    return false;
  }

  return partnersToShowLogoFor.some((metaPartner) => metaPartner.toLowerCase() === partner);
};

export const getIncludedBaggageText = ({ pieces, weight, weightUnit } = {}, textKey, t) => {
  const { LRE, PDF } = constants.unicodeControlCharacters;
  if (pieces && weight && weightUnit) {
    return t(textKey, `${LRE}${pieces}×${weight}${weightUnit}${PDF}`);
  }
  if (pieces) {
    return t(textKey, pieces);
  }
  if (pieces === null && weight && weightUnit) {
    return t(textKey, `${weight}${weightUnit}${PDF}`);
  }
  return '';
};

export const getFlightsExcludingSponsoredTripTags = (flights, selectedSortType) => {
  if (selectedSortType !== quickSortType.RECOMMENDED) {
    return flights;
  }

  const sponsoredTripTags = ['WEIGHTED', 'PRICE', 'SPEED'];

  return flights.filter((flight) =>
    sponsoredTripTags.every((tag) => !flight?.tripTags?.includes(tag)),
  );
};
